'use client';

import Box from '@enlighten/design-system/dist/Box';
import Button from '@enlighten/design-system/dist/Button';
import Checkbox from '@enlighten/design-system/dist/Checkbox';
import CircularProgress from '@enlighten/design-system/dist/CircularProgress';
import FormControlLabel from '@enlighten/design-system/dist/FormControlLabel';
import FormHelperText from '@enlighten/design-system/dist/FormHelperText';
import Grid from '@enlighten/design-system/dist/Grid';
import MenuItem from '@enlighten/design-system/dist/MenuItem';
import Stack from '@enlighten/design-system/dist/Stack';
import Typography from '@enlighten/design-system/dist/Typography';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import type { VppFormData } from '@/app/api/vpp/route';
import Arrow from '@/assets/images/arrow.svg';
import FormItem from '@/components/pages/common/FormItem';
import { useOnChangeMessage } from '@/contexts/layouts/MessageProvider';
import toPhoneNumFormat from '@/utils/format';

const INQUIRY_TYPE_OPTIONS = [
	{ label: 'VPP 전력중개 참여', value: 'vpp_brokerage' },
	{ label: '기업 PPA 공급 참여', value: 'corporate_ppa' },
	{ label: '발전왕 RTU 설치', value: 'rtu_installation' },
];

const defaultValues: VppFormData = {
	inquiryType: '',
	name: '',
	mobile: '',
	address: '',
	capacity: '',
	privacyPolicyAgreement: false,
};

const ConsultFormSection = () => {
	const [loading, setLoading] = useState(false);

	const onChangeMessage = useOnChangeMessage();

	const { control, handleSubmit, reset } = useForm<VppFormData>({ defaultValues, disabled: loading });

	const onSubmit = async (data: VppFormData) => {
		try {
			setLoading(true);
			const response = await fetch('/api/vpp', {
				method: 'POST',
				body: JSON.stringify(data),
			});

			if (response.ok) {
				onChangeMessage({
					type: 'success',
					message: '문의가 등록되었습니다.',
					duration: 4000,
				});
				reset(defaultValues);
			} else {
				onChangeMessage({
					type: 'error',
					message: '문의 등록에 실패했습니다. 잠시 후 다시 시도해 주세요.',
					duration: 4000,
				});
			}
		} catch (e) {
			console.error(e);
			onChangeMessage({
				type: 'error',
				message: '문의 등록에 실패했습니다. 잠시 후 다시 시도해 주세요.',
				duration: 4000,
			});
		} finally {
			setLoading(false);
		}
	};

	return (
		<Stack id="vpp-form" alignItems="center" padding={{ xs: '60px 20px', md: '100px 24px 140px' }} bgcolor={{ xs: '#F6F3FF', md: '#F9F6FF' }} sx={{ scrollMarginTop: '80px' }}>
			<Stack
				gap="40px"
				width="100%"
				maxWidth="680px"
				padding={{ xs: '40px 20px', md: '60px' }}
				bgcolor="common.white"
				border={{ xs: 'none', md: '1px solid #D6D7DA' }}
				borderRadius={{ xs: '14px', md: '16px' }}
				boxShadow={{ xs: '0 0 15px 0 rgba(0, 0, 0, 0.1)', md: '0 4px 14px 0 rgba(0, 0, 0, 0.1)' }}
			>
				<Stack gap={{ xs: '10px', md: '16px' }} textAlign="center" sx={{ wordBreak: 'keep-all' }}>
					<Typography fontSize={{ xs: '24px', md: '40px' }} fontWeight={700} lineHeight={{ xs: 1.4, md: '52px' }}>
						{'우리 발전소도\nVPP 운영할 수 있을까요?'}
					</Typography>
					<Typography fontSize={{ xs: '14px', md: '16px' }} lineHeight={{ xs: '24px', md: 1.4 }} color="#6F7276">
						발전소 위치와 용량만 알려주세요. 참여 가능한 제도와 예상 운영 방식을 안내해 드립니다.
					</Typography>
				</Stack>

				<Stack gap={{ xs: '24px', md: '40px' }}>
					<Stack gap="16px">
						<Grid container spacing="20px" columnSpacing="16px">
							<Grid size={12}>
								<Controller
									control={control}
									name="inquiryType"
									rules={{ required: '문의 유형을 선택해 주세요.' }}
									render={({ field: { value, onChange, disabled }, fieldState: { error } }) => (
										<FormItem
											disabled={disabled}
											select
											fullWidth
											label="문의 유형"
											required
											value={value}
											onChange={onChange}
											error={!!error}
											helperText={error?.message}
											slotProps={{
												select: {
													displayEmpty: true,
													// 다른 입력칸 placeholder 와 같은 opacity
													renderValue: (selected) =>
														selected ? (
															INQUIRY_TYPE_OPTIONS.find((option) => option.value === selected)?.label
														) : (
															<Box component="span" sx={{ opacity: 0.42 }}>
																선택해 주세요
															</Box>
														),
												},
											}}
										>
											{INQUIRY_TYPE_OPTIONS.map(({ label, value }) => (
												<MenuItem key={value} value={value}>
													{label}
												</MenuItem>
											))}
										</FormItem>
									)}
								/>
							</Grid>
							<Grid size={{ xs: 12, md: 6 }}>
								<Controller
									control={control}
									name="name"
									rules={{ required: '이름 또는 회사명을 입력해 주세요.' }}
									render={({ field: { value, onChange, disabled }, fieldState: { error } }) => (
										<FormItem disabled={disabled} fullWidth label="이름 / 회사명" required placeholder="홍길동" value={value} onChange={onChange} error={!!error} helperText={error?.message} />
									)}
								/>
							</Grid>
							<Grid size={{ xs: 12, md: 6 }}>
								<Controller
									control={control}
									name="mobile"
									rules={{
										required: '연락처를 입력해 주세요.',
										pattern: { value: /^[0-9]{2,3}[0-9]{3,4}[0-9]{4}$/, message: '올바른 형식으로 입력해 주세요.' },
									}}
									render={({ field: { value, onChange, disabled }, fieldState: { error } }) => (
										<FormItem
											disabled={disabled}
											fullWidth
											label="연락처"
											required
											placeholder="010-0000-0000"
											value={toPhoneNumFormat(value)}
											onChange={(e) => {
												onChange(e.target.value.replace(/[^0-9]/g, ''));
											}}
											error={!!error}
											helperText={error?.message}
											slotProps={{ input: { inputProps: { maxLength: 13, inputMode: 'numeric' } } }}
										/>
									)}
								/>
							</Grid>
							<Grid size={12}>
								<Controller
									control={control}
									name="address"
									rules={{ required: '발전소 위치를 입력해 주세요.' }}
									render={({ field: { value, onChange, disabled }, fieldState: { error } }) => (
										<FormItem
											disabled={disabled}
											fullWidth
											label="발전소 위치"
											required
											placeholder="예시 : 전남 해남군"
											value={value}
											onChange={onChange}
											error={!!error}
											helperText={error?.message}
										/>
									)}
								/>
							</Grid>
							<Grid size={12}>
								<Controller
									control={control}
									name="capacity"
									rules={{ required: '설비용량을 입력해 주세요.' }}
									render={({ field: { value, onChange, disabled }, fieldState: { error } }) => (
										<FormItem
											disabled={disabled}
											fullWidth
											label="설비용량 (kW)"
											required
											placeholder="예시 : 500"
											value={value}
											onChange={(e) => {
												onChange(
													e.target.value
														.replace(/[^0-9.]/g, '')
														.replace(/(\..*)\./g, '$1')
														.replace(/^0+(?=\d)/, '')
												);
											}}
											error={!!error}
											helperText={error?.message}
											slotProps={{ input: { inputProps: { inputMode: 'decimal' } } }}
										/>
									)}
								/>
							</Grid>
						</Grid>

						<Controller
							control={control}
							name="privacyPolicyAgreement"
							rules={{ required: '개인정보 수집 및 이용에 동의해 주세요.' }}
							render={({ field: { value, onChange, disabled }, fieldState: { error } }) => (
								<Box>
									<FormControlLabel
										control={<Checkbox disabled={disabled} />}
										checked={value}
										onChange={(_, checked) => {
											onChange(checked);
										}}
										label="상담을 위한 개인정보 수집 및 이용에 동의합니다."
										sx={{
											margin: 0,
											gap: '8px',
											'.MuiFormControlLabel-label': { fontSize: '14px', lineHeight: { xs: '24px', md: 1.5 }, color: '#6F7276', wordBreak: 'keep-all' },
										}}
									/>
									{error ? (
										<FormHelperText error sx={{ marginLeft: '32px' }}>
											{error.message}
										</FormHelperText>
									) : null}
								</Box>
							)}
						/>
					</Stack>

					<Button
						fullWidth
						disabled={loading}
						endIcon={loading ? <CircularProgress size={20} sx={{ color: 'grey.700' }} /> : <Arrow fill="white" />}
						onClick={handleSubmit(onSubmit)}
						sx={{
							height: 'auto',
							padding: { xs: '13px 24px', md: '16px 36px' },
							borderRadius: { xs: '4px', md: '6px' },
							fontSize: { xs: '16px', md: '18px' },
							lineHeight: { xs: '26px', md: '28px' },
						}}
					>
						무료 상담 신청
					</Button>
				</Stack>
			</Stack>
		</Stack>
	);
};

export default ConsultFormSection;
